import { api } from './api';
import type { Pagination } from './group.service';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface UserRef {
  id: string;
  full_name: string;
  avatar_url: string | null;
}

export interface GroupRef {
  id: string;
  name: string;
  reference_id: string;
}

export interface TagRef {
  id: string;
  name: string;
  color: string;
}

export interface DependencyRef {
  id: string;
  depends_on: { id: string; title: string; reference_id: string; status: string } | null;
}

export interface DependentRef {
  id: string;
  entity: { id: string; title: string; reference_id: string; status: string } | null;
}

export interface Goal {
  id: string;
  reference_id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  due_date: string;
  progress_percentage: number;
  total_tasks: number;
  completed_tasks: number;
  evaluation_score: number | null;
  owner_id: string;
  owner: UserRef;
  group_id: string;
  group: GroupRef;
  activated_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface GoalPermissions {
  can_edit: boolean;
  can_delete: boolean;
  can_change_status: boolean;
  can_manage_contributors: boolean;
  can_create_tasks: boolean;
  can_manage_dependencies: boolean;
}

export interface GoalDetail extends Goal {
  contributors: UserRef[];
  evaluators: UserRef[];
  tags: TagRef[];
  dependencies: DependencyRef[];
  dependents: DependentRef[];
  current_user_permissions?: GoalPermissions;
}

export interface GoalTask {
  id: string;
  reference_id: string;
  title: string;
  description: string | null;
  task_type: string;
  phase_status: string;
  calculated_status: string;
  priority: string;
  start_date: string | null;
  end_date: string | null;
  completed_at: string | null;
  assignee: UserRef | null;
  estimated_hours: number | null;
  actual_hours: number;
  created_at: string;
}

export interface DueDateHistoryItem {
  id: string;
  old_date: string;
  new_date: string;
  reason: string | null;
  changed_by: { id: string; full_name: string };
  created_at: string;
}

export interface Evaluation {
  id: string;
  entity_type: string;
  entity_id: string;
  evaluator_id: string;
  score: number;
  comment: string | null;
  evaluator: UserRef;
  created_at: string;
  updated_at: string;
}

export interface GoalListParams {
  cursor?: string;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  status?: string;
  priority?: string;
  group_id?: string;
  owner_id?: string;
  tag_id?: string;
}

export interface GoalTaskParams {
  cursor?: string;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  phase_status?: string;
  priority?: string;
  assignee_id?: string;
}

export interface CreateGoalData {
  title: string;
  description?: string;
  priority?: string;
  due_date: string;
  group_id: string;
  owner_id: string;
  contributor_ids?: string[];
  evaluator_ids?: string[];
  tag_ids?: string[];
}

export interface UpdateGoalData {
  title?: string;
  description?: string | null;
  priority?: string;
  due_date?: string;
  due_date_change_reason?: string;
  owner_id?: string;
  contributor_ids?: string[];
  evaluator_ids?: string[];
  tag_ids?: string[];
}

export const GOAL_STATUSES = ['draft', 'active', 'completed', 'completed_late', 'cancelled'] as const;
export const GOAL_PRIORITIES = ['low', 'medium', 'high', 'critical'] as const;

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------
export const goalService = {
  // CRUD
  list: (params?: GoalListParams) =>
    api.get<{ success: boolean; data: Goal[]; pagination: Pagination }>('/goals', { params }),

  get: (id: string) =>
    api.get<{ success: boolean; data: GoalDetail }>(`/goals/${id}`),

  create: (data: CreateGoalData) =>
    api.post<{ success: boolean; data: GoalDetail }>('/goals', data),

  update: (id: string, data: UpdateGoalData) =>
    api.patch<{ success: boolean; data: GoalDetail }>(`/goals/${id}`, data),

  delete: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/goals/${id}`),

  // Status transitions
  activate: (id: string) =>
    api.post<{ success: boolean; data: { status: string; activated_at: string } }>(`/goals/${id}/activate`),

  cancel: (id: string) =>
    api.post<{ success: boolean; data: { status: string; cancelled_at: string } }>(`/goals/${id}/cancel`),

  reopen: (id: string) =>
    api.post<{ success: boolean; data: { status: string } }>(`/goals/${id}/reopen`),

  // Tasks
  listTasks: (id: string, params?: GoalTaskParams) =>
    api.get<{ success: boolean; data: GoalTask[]; pagination: Pagination }>(`/goals/${id}/tasks`, { params }),

  // Due date history
  getDueDateHistory: (id: string) =>
    api.get<{ success: boolean; data: DueDateHistoryItem[] }>(`/goals/${id}/due-date-history`),

  // Dependencies
  addDependency: (id: string, dependsOnId: string) =>
    api.post<{ success: boolean; data: { id: string } }>(`/goals/${id}/dependencies`, { depends_on_id: dependsOnId }),

  removeDependency: (id: string, depId: string) =>
    api.delete<{ success: boolean; message: string }>(`/goals/${id}/dependencies/${depId}`),

  // Evaluations
  getEvaluations: (id: string) =>
    api.get<{ success: boolean; data: Evaluation[] }>(`/evaluations/goal/${id}`),
};
